import React, { useState, useEffect, useCallback } from 'react';
import { API_URL, getToken } from '../config';
import { useAuth } from '../contexts/AuthContext';

const adminFetch = async (path, options = {}) => {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!res.ok) throw new Error('Request failed');
  return res.json();
};

/**
 * OwnerDashboard — Inkwell editorial super-admin view.
 * Stats cards, tabbed sections (Teachers / Promo codes), monospace tables.
 */
export default function OwnerDashboard({ onBack }) {
  const { login } = useAuth();
  const [overview, setOverview] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tab, setTab] = useState('teachers');
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [genQuantity, setGenQuantity] = useState(10);
  const [genDuration, setGenDuration] = useState(30);
  const [genResult, setGenResult] = useState(null);

  const fetchPromoCodes = useCallback(async () => {
    try {
      const data = await adminFetch('/api/admin/promo-codes');
      setPromoCodes(data.codes || []);
    } catch {
      setError('Failed to load promo codes.');
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [overviewData, teachersData] = await Promise.all([
        adminFetch('/api/admin/overview'),
        adminFetch('/api/admin/teachers'),
      ]);
      setOverview(overviewData);
      setTeachers(teachersData.teachers || teachersData || []);
      await fetchPromoCodes();
    } catch {
      setError('Failed to load admin data. Make sure you have admin privileges.');
    } finally {
      setLoading(false);
    }
  }, [fetchPromoCodes]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (teacherId, action) => {
    setActionLoading(teacherId);
    try {
      if (action === 'delete') {
        await adminFetch(`/api/admin/teachers/${teacherId}`, { method: 'DELETE' });
      } else {
        await adminFetch(`/api/admin/teachers/${teacherId}/${action}`, { method: 'POST' });
      }
      setConfirmDelete(null);
      await fetchData();
    } catch {
      setError(`Action "${action}" failed.`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerate = async () => {
    setPromoLoading(true);
    setGenResult(null);
    try {
      const data = await adminFetch('/api/admin/promo-codes', {
        method: 'POST',
        body: JSON.stringify({ quantity: genQuantity, durationDays: genDuration }),
      });
      setGenResult(data.codes || []);
      await fetchPromoCodes();
    } catch {
      setError('Failed to generate promo codes.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleImpersonate = async (teacherId) => {
    setActionLoading(teacherId);
    try {
      const data = await adminFetch(`/api/admin/teachers/${teacherId}/impersonate`, {
        method: 'POST',
      });
      if (data.token && data.teacher) {
        login(data.token, data.teacher);
      }
    } catch {
      setError('Impersonation failed.');
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
        <header className="ink-header">
          <button className="btn btn--ghost btn--sm" onClick={onBack}>← Back</button>
          <span style={{ fontWeight: 500 }}>Owner dashboard</span>
          <div style={{ width: 80 }} />
        </header>
        <div style={{ padding: 64, textAlign: 'center' }}>
          <p className="t-body">Loading admin data…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <header className="ink-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button className="btn btn--ghost btn--sm" onClick={onBack}>← Back</button>
          <span style={{ fontWeight: 500 }}>Owner dashboard</span>
          <span className="badge badge--accent badge--mono">Super-admin</span>
        </div>
        <div style={{ width: 80 }} />
      </header>

      <div style={{ padding: '32px 40px' }}>
        {error && (
          <div
            style={{
              padding: '12px 16px',
              marginBottom: 24,
              background: 'var(--vermillion-50)',
              border: '1px solid var(--vermillion)',
              color: 'var(--vermillion-ink)',
              borderRadius: 'var(--r2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 14,
            }}
          >
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'transparent',
                border: 0,
                color: 'var(--vermillion-ink)',
                cursor: 'pointer',
                fontSize: 12,
                textDecoration: 'underline',
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Stats */}
        {overview && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 16,
              marginBottom: 32,
            }}
          >
            <div className="card" style={{ padding: 24 }}>
              <div className="t-eyebrow" style={{ marginBottom: 8 }}>Total teachers</div>
              <div style={{
                fontSize: 36, fontWeight: 500, lineHeight: 1, letterSpacing: '-0.02em',
              }}>
                {overview.totalTeachers ?? 0}
              </div>
            </div>
            <div className="card" style={{ padding: 24 }}>
              <div className="t-eyebrow" style={{ marginBottom: 8 }}>Pro teachers</div>
              <div style={{
                fontSize: 36, fontWeight: 500, lineHeight: 1, letterSpacing: '-0.02em',
              }}>
                {overview.proTeachers ?? 0}
              </div>
            </div>
            <div className="card" style={{ padding: 24 }}>
              <div className="t-eyebrow" style={{ marginBottom: 8 }}>Total words</div>
              <div style={{
                fontSize: 36, fontWeight: 500, lineHeight: 1, letterSpacing: '-0.02em',
              }}>
                {overview.totalWords ?? 0}
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--rule)', marginBottom: 24 }}>
          {[
            { key: 'teachers', label: 'Teachers' },
            { key: 'codes', label: 'Promo codes' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '12px 20px',
                background: 'transparent',
                border: 0,
                borderBottom: tab === t.key ? '2px solid var(--ink)' : '2px solid transparent',
                fontWeight: 500,
                color: tab === t.key ? 'var(--ink)' : 'var(--ink-3)',
                cursor: 'pointer',
                fontSize: 14,
                fontFamily: 'var(--font-sans)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'teachers' ? (
          <div className="card" style={{ overflow: 'hidden' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Plan</th>
                  <th>Words</th>
                  <th>Created</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teachers.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: 'var(--ink-3)' }}>
                      No teachers found.
                    </td>
                  </tr>
                )}
                {teachers.map((t) => (
                  <tr key={t.id}>
                    <td className="t-mono" style={{ fontSize: 12 }}>{t.email}</td>
                    <td>{t.name || '—'}</td>
                    <td>
                      {t.banned ? (
                        <span
                          className="badge"
                          style={{
                            background: 'var(--vermillion-50)',
                            color: 'var(--vermillion)',
                            borderColor: 'var(--vermillion)',
                          }}
                        >
                          Banned
                        </span>
                      ) : t.plan === 'pro' ? (
                        <span className="badge badge--gold">Pro</span>
                      ) : (
                        <span className="badge">Free</span>
                      )}
                    </td>
                    <td className="t-mono">{t.wordCount ?? '—'}</td>
                    <td className="t-small">
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        <button
                          className="btn btn--ghost btn--sm"
                          style={{ height: 28, padding: '0 8px', fontSize: 12 }}
                          disabled={actionLoading === t.id}
                          onClick={() =>
                            handleAction(t.id, t.plan === 'pro' ? 'downgrade' : 'upgrade')
                          }
                        >
                          {t.plan === 'pro' ? 'Downgrade' : 'Upgrade'}
                        </button>
                        <button
                          className="btn btn--ghost btn--sm"
                          style={{ height: 28, padding: '0 8px', fontSize: 12 }}
                          disabled={actionLoading === t.id}
                          onClick={() => handleImpersonate(t.id)}
                        >
                          Impersonate
                        </button>
                        <button
                          className="btn btn--ghost btn--sm"
                          style={{
                            height: 28,
                            padding: '0 8px',
                            fontSize: 12,
                            color: 'var(--vermillion)',
                          }}
                          disabled={actionLoading === t.id}
                          onClick={() => handleAction(t.id, t.banned ? 'unban' : 'ban')}
                        >
                          {t.banned ? 'Unban' : 'Ban'}
                        </button>
                        {confirmDelete === t.id ? (
                          <>
                            <button
                              className="btn btn--danger btn--sm"
                              style={{ height: 28, padding: '0 8px', fontSize: 12 }}
                              disabled={actionLoading === t.id}
                              onClick={() => handleAction(t.id, 'delete')}
                            >
                              Confirm
                            </button>
                            <button
                              className="btn btn--ghost btn--sm"
                              style={{ height: 28, padding: '0 8px', fontSize: 12 }}
                              onClick={() => setConfirmDelete(null)}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn--ghost btn--sm"
                            style={{
                              height: 28,
                              padding: '0 8px',
                              fontSize: 12,
                              color: 'var(--vermillion)',
                            }}
                            disabled={actionLoading === t.id}
                            onClick={() => setConfirmDelete(t.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <div className="card" style={{ padding: 24, marginBottom: 24 }}>
              <h2 className="t-h3" style={{ marginBottom: 16 }}>Generate codes</h2>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div className="field" style={{ width: 140 }}>
                  <label className="field-label">Quantity</label>
                  <select
                    className="select"
                    value={genQuantity}
                    onChange={(e) => setGenQuantity(Number(e.target.value))}
                  >
                    {[5, 10, 20].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div className="field" style={{ width: 160 }}>
                  <label className="field-label">Duration</label>
                  <select
                    className="select"
                    value={genDuration}
                    onChange={(e) => setGenDuration(Number(e.target.value))}
                  >
                    {[30, 60, 90].map((n) => (
                      <option key={n} value={n}>{n} days</option>
                    ))}
                  </select>
                </div>
                <button
                  className="btn btn--accent"
                  onClick={handleGenerate}
                  disabled={promoLoading}
                >
                  {promoLoading ? 'Generating…' : 'Generate'}
                </button>
              </div>

              {genResult && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 16,
                    background: 'var(--paper-2)',
                    border: '1px solid var(--rule)',
                    borderRadius: 'var(--r2)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <strong style={{ fontSize: 14 }}>
                      Generated {genResult.length} codes
                    </strong>
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() => navigator.clipboard.writeText(genResult.join('\n'))}
                    >
                      Copy all
                    </button>
                  </div>
                  <pre
                    className="t-mono"
                    style={{
                      margin: 0,
                      padding: 12,
                      background: 'var(--card)',
                      borderRadius: 'var(--r1)',
                      fontSize: 12,
                      color: 'var(--ink-2)',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {genResult.join('\n')}
                  </pre>
                </div>
              )}
            </div>

            <div className="card" style={{ overflow: 'hidden' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Redeemed by</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-3)' }}>
                        No promo codes yet. Generate some above.
                      </td>
                    </tr>
                  )}
                  {promoCodes.map((c) => (
                    <tr key={c.code}>
                      <td className="t-mono" style={{ fontWeight: 500 }}>{c.code}</td>
                      <td>{c.durationDays} days</td>
                      <td>
                        {c.redeemedBy ? (
                          <span className="badge">Redeemed</span>
                        ) : (
                          <span className="badge badge--jade">Available</span>
                        )}
                      </td>
                      <td className="t-small">{c.redeemedAt || '—'}</td>
                      <td className="t-small">{c.createdAt}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
